import { FormItem, Input, Label, Text } from '@ui5/webcomponents-react';
import { SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import { IssueType, AbapQueryDetailsFragment, useUpdateAbapQueryMutation } from 'generated/graphql';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdateBaseQueryStore } from 'stores/UpdateBaseQueryStore';
import { useEditQueryStore } from 'stores/EditQueryStore';
import { validateString } from 'utils/validator';
import { FormDisplayItem } from '..';
import { useStyles } from '../DisplayQuery.jss';
import dayjs from 'dayjs';
import { useUpdateABAPQueryStore } from 'stores/UpdateABAPQueryStore';

import WrappingType from '@ui5/webcomponents/dist/types/WrappingType.js';

export interface DisplayQueryDetailsABAPProps {
  queryId: string;
  queryDetails: AbapQueryDetailsFragment;
}

export const DisplayQueryDetailsABAP = forwardRef<SpecificQueryType, DisplayQueryDetailsABAPProps>(
  (props: DisplayQueryDetailsABAPProps, ref) => {
    const { queryDetails, queryId } = props;
    const { t } = useTranslation();
    const abapQueryStore = useUpdateABAPQueryStore();
    const baseQueryStore = useUpdateBaseQueryStore();
    const { editMode, setCancelEditing } = useEditQueryStore();
    const [, callUpdateQuery] = useUpdateAbapQueryMutation();
    const classes = useStyles();

    useImperativeHandle(ref, () => ({
      cancel: () => {
        abapQueryStore.setInitialStateWithProps(queryDetails);
      },
      save: () => {
        return saveQuery();
      },
      isValid: () => {
        return validateAllFields();
      }
    }));

    useEffect(() => {
      abapQueryStore.setInitialStateWithProps(queryDetails);
    }, []);

    const onChangeSystem = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      abapQueryStore.setSystem(value);
      validateString(value, abapQueryStore.setSystemValueState);
    };
    const onChangeObjectType = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      abapQueryStore.setObjectType(value);
      validateString(value, abapQueryStore.setObjectTypeValueState);
    };
    const onChangeObjectName = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      abapQueryStore.setObjectName(value);
      validateString(value, abapQueryStore.setObjectNameValueState);
    };
    const onChangeObjectOwner = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      abapQueryStore.setObjectOwner(value);
    };
    const onChangeCollectionPackage = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      abapQueryStore.setCollectionPackage(value);
      validateString(value, abapQueryStore.setCollectionPackageValueState);
    };
    const onChangeCollectionOwner = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      abapQueryStore.setCollectionOwner(value);
    };
    const onChangeMaxLength = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      abapQueryStore.setMaxLength(value != '' ? Number(value) : null);
    };
    const onChangeApplicationComponent = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      abapQueryStore.setApplicationComponent(value);
      validateString(value, abapQueryStore.setApplicationComponentValueState);
    };
    const onChangeRepositoryProgramType = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      abapQueryStore.setRepositoryProgramType(value);
      validateString(value, abapQueryStore.setRepositoryProgramTypeValueState);
    };
    const onChangeRepositoryProgramId = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      abapQueryStore.setRepositoryProgramId(value);
      validateString(value, abapQueryStore.setRepositoryProgramIdValueState);
    };
    const onChangeRepositoryProgramName = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      abapQueryStore.setRepositoryProgramName(value);
      validateString(value, abapQueryStore.setRepositoryProgramNameValueState);
    };
    const onChangeTranslationArea = (event: CustomEvent) => {
      setCancelEditing(false);
      const { value } = event.target as HTMLInputElement;
      abapQueryStore.setTranslationArea(value);
      validateString(value, abapQueryStore.setTranslationAreaValueState);
    };

    const saveQuery = async () => {
      const result = await callUpdateQuery({
        input: {
          id: Number(queryId),
          sourceText: baseQueryStore.sourceText,
          targetText: baseQueryStore.targetText,
          deadline: dayjs(baseQueryStore.deadline).format('YYYY-MM-DD'),
          targetLanguage: baseQueryStore.targetLanguage,
          issueType: baseQueryStore.selectedIssueType as unknown as IssueType,
          details: baseQueryStore.details,
          ...(baseQueryStore.assignee && { assignee: baseQueryStore.assignee }),
          distributions: baseQueryStore.distributions,
          resetedDistributions: baseQueryStore.resetedDistributions,
          system: abapQueryStore.system,
          objectType: abapQueryStore.objectType,
          objectName: abapQueryStore.objectName,
          objectOwner: abapQueryStore.objectOwner,
          collectionPackage: abapQueryStore.collectionPackage,
          collectionOwner: abapQueryStore.collectionOwner,
          maxLength: abapQueryStore.maxLength,
          applicationComponent: abapQueryStore.applicationComponent,
          repositoryProgramType: abapQueryStore.repositoryProgramType,
          repositoryProgramId: abapQueryStore.repositoryProgramId,
          repositoryProgramName: abapQueryStore.repositoryProgramName,
          translationArea: abapQueryStore.translationArea,
          qeReview: baseQueryStore.qeReview
        }
      });
      return { error: result.error };
    };

    const validateAllFields = () => {
      const isSystemValid = validateSystem();
      const isObjectTypeValid = validateObjectType();
      const isObjectNameValid = validateObjectName();
      const isCollectionPackageValid = validateCollectionPackage();
      const isApplicationComponentValid = validateApplicationComponent();
      const isRepositoryProgramTypeValid = validateRepositoryProgramType();
      const isRepositoryProgramIdValid = validateRepositoryProgramId();
      const isRepositoryProgramNameValid = validateRepositoryProgramName();
      const isTranslationAreaValid = validateTranslationArea();
      return (
        isSystemValid &&
        isObjectTypeValid &&
        isObjectNameValid &&
        isCollectionPackageValid &&
        isApplicationComponentValid &&
        isRepositoryProgramTypeValid &&
        isRepositoryProgramIdValid &&
        isRepositoryProgramNameValid &&
        isTranslationAreaValid
      );
    };

    const validateSystem = () => {
      return validateString(abapQueryStore.system, abapQueryStore.setSystemValueState);
    };
    const validateObjectType = () => {
      return validateString(abapQueryStore.objectType, abapQueryStore.setObjectTypeValueState);
    };
    const validateObjectName = () => {
      return validateString(abapQueryStore.objectName, abapQueryStore.setObjectNameValueState);
    };
    const validateCollectionPackage = () => {
      return validateString(
        abapQueryStore.collectionPackage,
        abapQueryStore.setCollectionPackageValueState
      );
    };
    const validateApplicationComponent = () => {
      return validateString(
        abapQueryStore.applicationComponent,
        abapQueryStore.setApplicationComponentValueState
      );
    };
    const validateRepositoryProgramType = () => {
      return validateString(
        abapQueryStore.repositoryProgramType,
        abapQueryStore.setRepositoryProgramTypeValueState
      );
    };
    const validateRepositoryProgramId = () => {
      return validateString(
        abapQueryStore.repositoryProgramId,
        abapQueryStore.setRepositoryProgramIdValueState
      );
    };
    const validateRepositoryProgramName = () => {
      return validateString(
        abapQueryStore.repositoryProgramName,
        abapQueryStore.setRepositoryProgramNameValueState
      );
    };
    const validateTranslationArea = () => {
      return validateString(
        abapQueryStore.translationArea,
        abapQueryStore.setTranslationAreaValueState
      );
    };

    const getDetailsData = (): FormDisplayItem[] => {
      return [
        {
          renderComponent: () => (
            <Input
              value={abapQueryStore.system}
              aria-labelledby={t('display-query-details-abap-system')}
              data-testid="edit-system"
              data-help-id="edit-system"
              className={classes.formLineInput}
              valueState={abapQueryStore.systemValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-system')}
                </Label>
              }
              onChange={onChangeSystem}
            />
          ),
          dataTestId: 'system',
          data: abapQueryStore.system,
          i18nTag: t('display-query-details-abap-system'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={abapQueryStore.translationArea}
              aria-labelledby={t('display-query-details-abap-translation-area')}
              data-testid="edit-translation-area"
              data-help-id="edit-translation-area"
              className={classes.formLineInput}
              valueState={abapQueryStore.translationAreaValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-translation-area')}
                </Label>
              }
              onChange={onChangeTranslationArea}
            />
          ),
          dataTestId: 'translation-area',
          data: abapQueryStore.translationArea,
          i18nTag: t('display-query-details-abap-translation-area'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={abapQueryStore.objectType}
              aria-labelledby={t('display-query-details-abap-object-type')}
              data-testid="edit-object-type"
              data-help-id="edit-object-type"
              className={classes.formLineInput}
              valueState={abapQueryStore.objectTypeValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-object-type')}
                </Label>
              }
              onChange={onChangeObjectType}
            />
          ),
          dataTestId: 'object-type',
          data: abapQueryStore.objectType,
          i18nTag: t('display-query-details-abap-object-type'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={abapQueryStore.objectName}
              aria-labelledby={t('display-query-details-abap-object-name')}
              data-testid="edit-object-name"
              data-help-id="edit-object-name"
              className={classes.formLineInput}
              valueState={abapQueryStore.objectNameValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-object-name')}
                </Label>
              }
              onChange={onChangeObjectName}
            />
          ),
          dataTestId: 'object-name',
          data: abapQueryStore.objectName,
          i18nTag: t('display-query-details-abap-object-name'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={abapQueryStore.collectionPackage}
              aria-labelledby={t('display-query-details-abap-collection-package')}
              data-testid="edit-collection-package"
              data-help-id="edit-collection-package"
              className={classes.formLineInput}
              valueState={abapQueryStore.collectionPackageValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-collection-package')}
                </Label>
              }
              onChange={onChangeCollectionPackage}
            />
          ),
          dataTestId: 'collection-package',
          data: abapQueryStore.collectionPackage,
          i18nTag: t('display-query-details-abap-collection-package'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={abapQueryStore.applicationComponent}
              aria-labelledby={t('display-query-details-abap-application-component')}
              data-testid="edit-application-component"
              data-help-id="edit-application-component"
              className={classes.formLineInput}
              valueState={abapQueryStore.applicationComponentValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-application-component')}
                </Label>
              }
              onChange={onChangeApplicationComponent}
            />
          ),
          dataTestId: 'application-component',
          data: abapQueryStore.applicationComponent,
          i18nTag: t('display-query-details-abap-application-component'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={abapQueryStore.repositoryProgramId}
              aria-labelledby={t('display-query-details-abap-repository-program-id')}
              data-testid="edit-repository-program-id"
              data-help-id="edit-repository-program-id"
              className={classes.formLineInput}
              valueState={abapQueryStore.repositoryProgramIdValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-repository-program-id')}
                </Label>
              }
              onChange={onChangeRepositoryProgramId}
            />
          ),
          dataTestId: 'repository-program-id',
          data: abapQueryStore.repositoryProgramId,
          i18nTag: t('display-query-details-abap-repository-program-id'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={abapQueryStore.repositoryProgramType}
              aria-labelledby={t('display-query-details-abap-repository-program-type')}
              data-testid="edit-repository-program-type"
              data-help-id="edit-repository-program-type"
              className={classes.formLineInput}
              valueState={abapQueryStore.repositoryProgramTypeValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-repository-program-type')}
                </Label>
              }
              onChange={onChangeRepositoryProgramType}
            />
          ),
          dataTestId: 'repository-program-type',
          data: abapQueryStore.repositoryProgramType,
          i18nTag: t('display-query-details-abap-repository-program-type'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={abapQueryStore.repositoryProgramName}
              aria-labelledby={t('display-query-details-abap-repository-program-name')}
              data-testid="edit-repository-program-name"
              data-help-id="edit-repository-program-name"
              className={classes.formLineInput}
              valueState={abapQueryStore.repositoryProgramNameValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-repository-program-name')}
                </Label>
              }
              onChange={onChangeRepositoryProgramName}
            />
          ),
          dataTestId: 'repository-program-name',
          data: abapQueryStore.repositoryProgramName,
          i18nTag: t('display-query-details-abap-repository-program-name'),
          required: true
        },
        {
          renderComponent: () => (
            <Input
              value={abapQueryStore.objectOwner ?? ''}
              aria-labelledby={t('display-query-details-abap-object-owner')}
              data-testid="edit-object-owner"
              data-help-id="edit-object-owner"
              className={classes.formLineInput}
              valueState={abapQueryStore.objectOwnerValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-object-owner')}
                </Label>
              }
              onChange={onChangeObjectOwner}
            />
          ),
          dataTestId: 'object-owner',
          data: abapQueryStore.objectOwner,
          i18nTag: t('display-query-details-abap-object-owner'),
          required: false
        },
        {
          renderComponent: () => (
            <Input
              value={abapQueryStore.collectionOwner ?? ''}
              aria-labelledby={t('display-query-details-abap-collection-owner')}
              data-testid="edit-collection-owner"
              data-help-id="edit-collection-owner"
              className={classes.formLineInput}
              valueState={abapQueryStore.collectionOwnerValueState}
              valueStateMessage={
                <Label wrappingType={WrappingType.Normal}>
                  {t('display-value-state-message-collection-owner')}
                </Label>
              }
              onChange={onChangeCollectionOwner}
            />
          ),
          dataTestId: 'collection-owner',
          data: abapQueryStore.collectionOwner,
          i18nTag: t('display-query-details-abap-collection-owner'),
          required: false
        },
        {
          renderComponent: () => (
            <Input
              value={String(abapQueryStore.maxLength ?? '')}
              aria-labelledby={t('display-query-details-abap-max-length')}
              data-testid="edit-max-length"
              data-help-id="edit-max-length"
              className={classes.formLineInput}
              type="Number"
              onChange={onChangeMaxLength}
            />
          ),
          dataTestId: 'max-length',
          data: String(abapQueryStore.maxLength ?? ''),
          i18nTag: t('display-query-details-abap-max-length'),
          required: false
        }
      ];
    };

    let queryDetailsData: FormDisplayItem[] = getDetailsData();

    useEffect(() => {
      queryDetailsData = getDetailsData();
    }, [queryDetails]);

    return (
      <>
        {queryDetailsData.map((entry) => (
          <FormItem
            key={entry.dataTestId}
            labelContent={
              <Label
                id={entry.i18nTag}
                required={editMode && entry.required}
                data-help-id={entry.dataTestId}
                className={classes.formItemLabel}
                wrappingType="None"
              >
                {entry.i18nTag}
              </Label>
            }
          >
            {editMode && entry.editable !== false ? (
              entry.renderComponent()
            ) : (
              <Text
                data-testid={entry.dataTestId}
                aria-labelledby={entry.i18nTag}
                className={classes.formItemText}
              >
                {entry.data}
              </Text>
            )}
          </FormItem>
        ))}
      </>
    );
  }
);
